/* ================================================================
   ProAdvLogo — componente de marca reutilizavel.

   Props:
     variant  'horizontal' | 'stacked' | 'icon' | 'text'  (default 'horizontal')
     size     altura base em px                             (default 32)
     tone     'dark' | 'light'                             (default 'dark')
   ================================================================ */

export type ProAdvLogoVariant = 'horizontal' | 'stacked' | 'icon' | 'text';
export type ProAdvLogoTone = 'dark' | 'light';

export interface ProAdvLogoProps {
  variant?: ProAdvLogoVariant;
  size?: number;
  tone?: ProAdvLogoTone;
}

// Paleta por tom
const PALETTE = {
  dark: {
    icon: '#E8B96A',
    pro: '#F5F1FA',
    adv: '#E8B96A',
  },
  light: {
    icon: '#6D28D9',
    pro: '#1B1029',
    adv: '#6D28D9',
  },
} as const;

/* ================================================================
   Sub-componente: icone SVG do martelo (gavel) diagonal
   ================================================================ */
function GavelIcon({ size, tone }: { size: number; tone: ProAdvLogoTone }) {
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      stroke={PALETTE[tone].icon}
      strokeLinecap="round"
      aria-hidden="true"
      style={{ width: size, height: size, flexShrink: 0, display: 'block' }}
    >
      <line x1="33" y1="21" x2="21" y2="33" strokeWidth="11" />
      <line x1="27" y1="27" x2="45" y2="45" strokeWidth="6" />
    </svg>
  );
}

/* ================================================================
   Sub-componente: wordmark "ProAdv"
   ================================================================ */
function Wordmark({ fontSize, tone }: { fontSize: number; tone: ProAdvLogoTone }) {
  return (
    <span
      style={{
        fontFamily: "'Fraunces', Georgia, serif",
        fontWeight: 600,
        fontSize,
        letterSpacing: '-0.02em',
        lineHeight: 1,
        display: 'inline-block',
      }}
    >
      <span style={{ color: PALETTE[tone].pro }}>Voxis</span>
      <span style={{ color: PALETTE[tone].adv }}>Adv</span>
    </span>
  );
}

/* ================================================================
   Componente principal
   ================================================================ */
export function ProAdvLogo({
  variant = 'horizontal',
  size = 32,
  tone = 'dark',
}: ProAdvLogoProps) {
  const fontSize = Math.round(size * 0.8);

  if (variant === 'icon') {
    return (
      <span
        role="img"
        aria-label="VoxisAdv"
        style={{ display: 'inline-flex', width: size, height: size }}
      >
        <GavelIcon size={size} tone={tone} />
      </span>
    );
  }

  if (variant === 'text') {
    return (
      <span role="img" aria-label="VoxisAdv" style={{ display: 'inline-flex' }}>
        <Wordmark fontSize={fontSize} tone={tone} />
      </span>
    );
  }

  if (variant === 'stacked') {
    const iconSize = Math.round(size * 1.3);
    return (
      <span
        role="img"
        aria-label="VoxisAdv"
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: Math.round(size * 0.18),
        }}
      >
        <GavelIcon size={iconSize} tone={tone} />
        <Wordmark fontSize={fontSize} tone={tone} />
      </span>
    );
  }

  // horizontal (default)
  return (
    <span
      role="img"
      aria-label="VoxisAdv"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: Math.round(size * 0.35),
      }}
    >
      <GavelIcon size={size} tone={tone} />
      <Wordmark fontSize={fontSize} tone={tone} />
    </span>
  );
}
